import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, collectionGroup } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { db } from './../../../firebase.js';
import { getCategoryColor } from './../../../common/categoryColors.js';
import './InferenceTimesPage.css';

function InferenceTimesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [inferenceAveragesByLabel, setInferenceAveragesByLabel] = useState({});
    const [inferenceTimes, setInferenceTimes] = useState([]);
    const [latestInferenceByUnit, setLatestInferenceByUnit] = useState({});
    const [unitIds, setUnitIds] = useState([]);
    const [unitCategoryById, setUnitCategoryById] = useState({});
    const [unitNavigationById, setUnitNavigationById] = useState({});
    const isRangeActive = (range, now) => {
        if (!range || !range.unit_id) return false;
        if (range.current === true) return true;
        if (range.start_date === undefined || range.start_date === null) return false;
        if (range.end_date === undefined || range.end_date === null) return false;
        return now >= range.start_date && now <= range.end_date;
    };

    useEffect(() => {
        let recordUnsubscribers = [];

        const unitsUnsubscribe = onSnapshot(collection(db, "units"), (unitsSnapshot) => {
            recordUnsubscribers.forEach((unsubscribe) => unsubscribe());
            recordUnsubscribers = [];

            const nextUnitIds = [];
            const activeUnitIds = new Set();
            const nextUnitCategoryById = {};

            unitsSnapshot.forEach((unitDoc) => {
                const unit = unitDoc.data() || {};
                const unitId = unit.unit_id || unitDoc.id;
                if (!unitId) return;
                const unitCategory = (unit.deployment_config && unit.deployment_config.category) || "Uncategorized";

                activeUnitIds.add(unitId);
                nextUnitIds.push(unitId);
                nextUnitCategoryById[unitId] = unitCategory;

                const isDangerousWasteUnit = unitCategory === "dangerous_waste";
                const recordsCollectionName = isDangerousWasteUnit ? "dangerous-waste-records" : "inference-records";
                const latestRecordQuery = query(
                    collection(db, "units", unitId, recordsCollectionName),
                    orderBy("timestamp", "desc"),
                    limit(10)
                );

                const recordUnsubscribe = onSnapshot(latestRecordQuery, (recordSnapshot) => {
                    const firstNonTest = recordSnapshot.docs.find((d) => {
                        const data = d.data() || {};
                        return data.is_test !== true;
                    });

                    if (!firstNonTest) {
                        setLatestInferenceByUnit((prev) => {
                            if (!(unitId in prev)) return prev;
                            const next = { ...prev };
                            delete next[unitId];
                            return next;
                        });
                        return;
                    }

                    const recordData = firstNonTest.data() || {};
                    const latestInferenceTime = Number(recordData.inference_time);
                    if (Number.isFinite(latestInferenceTime)) {
                        setLatestInferenceByUnit((prev) => ({ ...prev, [unitId]: latestInferenceTime }));
                    }
                }, (error) => {
                    console.log("Error listening to latest record for unit:", unitId, error);
                });

                recordUnsubscribers.push(recordUnsubscribe);
            });

            setUnitIds(nextUnitIds);
            setUnitCategoryById(nextUnitCategoryById);
            setLatestInferenceByUnit((prev) => {
                const next = {};
                Object.keys(prev).forEach((unitId) => {
                    if (activeUnitIds.has(unitId)) next[unitId] = prev[unitId];
                });
                return next;
            });
        }, (error) => {
            console.log("Error listening to units:", error);
        });

        return () => {
            recordUnsubscribers.forEach((unsubscribe) => unsubscribe());
            unitsUnsubscribe();
        };
    }, []);

    useEffect(() => {
        const inferenceUnsubscribe = onSnapshot(doc(db, "admin", "inference_time"), (inferenceDoc) => {
            setInferenceAveragesByLabel(inferenceDoc.exists() ? (inferenceDoc.data() || {}) : {});
        }, (error) => {
            console.log("Error listening to inference-time averages:", error);
        });

        return () => inferenceUnsubscribe();
    }, []);

    useEffect(() => {
        const locationsUnsubscribe = onSnapshot(collectionGroup(db, "locations"), (locationsSnapshot) => {
            const now = Date.now();
            const nextNavigationById = {};

            locationsSnapshot.forEach((locationDoc) => {
                const locationData = locationDoc.data() || {};
                const locationId = locationDoc.id;

                if (Array.isArray(locationData.display_units)) {
                    locationData.display_units.forEach((displayUnit, displayUnitIndex) => {
                        const unitRanges = Array.isArray(displayUnit?.unit_ranges) ? displayUnit.unit_ranges : [];
                        unitRanges.forEach((range) => {
                            if (!isRangeActive(range, now)) return;
                            const unitId = range.unit_id;
                            if (!unitId || nextNavigationById[unitId]) return;
                            nextNavigationById[unitId] = {
                                type: "display",
                                locationId,
                                targetId: displayUnitIndex
                            };
                        });
                    });
                }

                if (Array.isArray(locationData.units)) {
                    locationData.units.forEach((unitId) => {
                        if (!unitId || nextNavigationById[unitId]) return;
                        nextNavigationById[unitId] = {
                            type: "legacy",
                            locationId,
                            targetId: unitId
                        };
                    });
                }
            });

            setUnitNavigationById(nextNavigationById);
        }, (error) => {
            console.log("Error listening to locations for inference card navigation:", error);
        });

        return () => locationsUnsubscribe();
    }, []);

    const navigateToUnitDashboard = (unitId) => {
        const target = unitNavigationById[unitId];
        if (!target) return;

        navigate(`/locations/${target.locationId}/unit/${target.targetId}/dashboard`);
    };

    const getSortedCategories = () => {
        return Array.from(new Set(Object.values(unitCategoryById))).sort((a, b) => a.localeCompare(b));
    };

    const hexToRgb = (hex) => {
        const normalized = String(hex || '').replace('#', '');
        const bigint = Number.parseInt(normalized, 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255
        };
    };

    const getCategoryBaseColor = (categoryLabel) => {
        const sortedCategories = getSortedCategories();
        const categoryIndex = sortedCategories.indexOf(categoryLabel);
        // +1 to mirror AdminPage where index 0 is reserved for "Total".
        const paletteIndex = categoryIndex >= 0 ? categoryIndex + 1 : 1;
        return getCategoryColor(categoryLabel, paletteIndex);
    };

    const getBadgeStyle = (inference) => {
        if (inference.type === "total") {
            return {
                borderColor: 'rgba(155, 231, 185, 0.45)',
                background: 'rgba(95, 188, 130, 0.2)',
                color: '#b8f0ce'
            };
        }

        const categoryLabel = inference.type === "unit"
            ? (unitCategoryById[inference.label] || "Uncategorized")
            : inference.label;
        const baseColor = getCategoryBaseColor(categoryLabel);
        const rgb = hexToRgb(baseColor);

        if (inference.type === "category") {
            const darkR = Math.round(rgb.r * 0.58);
            const darkG = Math.round(rgb.g * 0.58);
            const darkB = Math.round(rgb.b * 0.58);
            return {
                borderColor: `rgba(${darkR}, ${darkG}, ${darkB}, 0.9)`,
                background: `rgba(${darkR}, ${darkG}, ${darkB}, 0)`,
                color: `rgb(${Math.min(255, rgb.r + 40)}, ${Math.min(255, rgb.g + 40)}, ${Math.min(255, rgb.b + 40)})`
            };
        }

        return {
            borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`,
            background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`,
            color: `rgb(${Math.min(255, rgb.r + 40)}, ${Math.min(255, rgb.g + 40)}, ${Math.min(255, rgb.b + 40)})`
        };
    };

    useEffect(() => {
        const unitIdSet = new Set(unitIds);
        const entries = Object.entries(inferenceAveragesByLabel)
            .filter(([, value]) => value === null || Number.isFinite(Number(value)))
            .map(([label, value]) => ({
                label,
                type: label === "total" ? "total" : (unitIdSet.has(label) ? "unit" : "category"),
                average: value === null ? null : Number(value)
            }))
            .sort((a, b) => {
                const typeRank = { total: 0, category: 1, unit: 2 };
                if (typeRank[a.type] !== typeRank[b.type]) return typeRank[a.type] - typeRank[b.type];

                // Group unit cards by category before sorting by unit label.
                if (a.type === "unit" && b.type === "unit") {
                    const categoryA = unitCategoryById[a.label] || "Uncategorized";
                    const categoryB = unitCategoryById[b.label] || "Uncategorized";
                    const categoryCompare = categoryA.localeCompare(categoryB);
                    if (categoryCompare !== 0) return categoryCompare;
                    return a.label.localeCompare(b.label);
                }

                return a.label.localeCompare(b.label);
            });
        setInferenceTimes(entries);
    }, [inferenceAveragesByLabel, unitIds, unitCategoryById]);

    return (
        <div className="inference-times-page">
            <div className="inference-times-grid">
                {
                    inferenceTimes.map((inference, index) => (
                        <div
                            key={index}
                            className={`inference-time-card ${inference.type === "unit" && unitNavigationById[inference.label] ? "clickable" : ""}`}
                            onClick={() => {
                                if (inference.type === "unit") {
                                    navigateToUnitDashboard(inference.label);
                                }
                            }}
                            role={inference.type === "unit" && unitNavigationById[inference.label] ? "button" : undefined}
                            tabIndex={inference.type === "unit" && unitNavigationById[inference.label] ? 0 : undefined}
                            onKeyDown={(event) => {
                                if (inference.type !== "unit" || !unitNavigationById[inference.label]) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    navigateToUnitDashboard(inference.label);
                                }
                            }}
                        >
                            <div className="inference-time-header">
                                <div className="inference-time-unit">
                                    {inference.type === "total" ? t("Total") : t(inference.label)}
                                </div>
                                <div className={`inference-type-badge ${inference.type}`} style={getBadgeStyle(inference)}>
                                    {inference.type === "unit"
                                        ? t(unitCategoryById[inference.label] || "Uncategorized")
                                        : (inference.type === "total" ? t("Total") : t("Category"))}
                                </div>
                            </div>
                            <div className="separator"></div>
                            <div className="inference-time-content">
                                {inference.type === "unit" && Number.isFinite(latestInferenceByUnit[inference.label]) && (
                                    <>
                                        <div className="last-inference-time"><span className="data-value">{latestInferenceByUnit[inference.label].toFixed(2)}s</span></div>
                                        ·
                                    </>
                                )}
                                <div className="average-inference-time">7-day: {inference.average !== null
                                    ? <span className="data-value">{inference.average.toFixed(2)}s</span>
                                    : <span className="data-value-none">{t("None")}</span>
                                }</div>
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    );
}

export default InferenceTimesPage;
